import React, { useEffect, useRef } from 'react';
import { Button, useTheme } from '@mui/material';
import classNames from 'classnames';

const config = require('config/app.config');

declare global {
    interface Window {
        google: any;
    }
}

type GoogleSignInButtonProps = {
    onSuccess: (credential: string) => void;
    onError?: (error: string) => void;
    className?: string;
    disabled?: boolean;
    text?: string;
};

function GoogleSignInButton({
    onSuccess,
    onError,
    className,
    disabled = false,
    text = 'Continue with Google',
}: GoogleSignInButtonProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const buttonRef = useRef<HTMLDivElement>(null);
    const googleLoadedRef = useRef(false);

    useEffect(() => {
        const googleClientId = config.google.client_id;

        if (!googleClientId) {
            console.warn('Google Client ID not configured');
            if (onError) {
                onError('Google authentication is not configured');
            }
            return;
        }

        // Check if Google Identity Services is already loaded
        if (window.google && googleLoadedRef.current) {
            initializeGoogleSignIn();
            return;
        }

        // Load Google Identity Services script
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;

        script.onload = () => {
            window.google.accounts.id.initialize({
                client_id: googleClientId,
                callback: (response: any) => {
                    if (response.credential) {
                        onSuccess(response.credential);
                    } else if (onError) {
                        onError('Failed to get Google credential');
                    }
                },
            });
            googleLoadedRef.current = true;
            renderButton();
        };

        script.onerror = () => {
            console.error('Failed to load Google Identity Services');
            if (onError) {
                onError('Failed to load Google authentication');
            }
        };

        document.body.appendChild(script);

        return () => {
            // Cleanup: remove script if component unmounts
            const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
            if (existingScript) {
                // Don't remove script as it might be used by other components
                // Just clear the button
                if (buttonRef.current) {
                    buttonRef.current.innerHTML = '';
                }
            }
        };
    }, []);

    const initializeGoogleSignIn = () => {
        if (window.google && buttonRef.current) {
            window.google.accounts.id.initialize({
                client_id: config.google.client_id,
                callback: (response: any) => {
                    if (response.credential) {
                        onSuccess(response.credential);
                    } else if (onError) {
                        onError('Failed to get Google credential');
                    }
                },
            });
            renderButton();
        }
    };

    const renderButton = () => {
        if (window.google && buttonRef.current && !disabled) {
            // Clear any existing button first
            buttonRef.current.innerHTML = '';
            window.google.accounts.id.renderButton(buttonRef.current, {
                theme: isDark ? 'filled_black' : 'outline',
                size: 'large',
                width: '100%',
                text: 'signin_with',
            });
        }
    };

    useEffect(() => {
        if (googleLoadedRef.current && buttonRef.current) {
            if (disabled) {
                // Clear button when disabled
                if (buttonRef.current) {
                    buttonRef.current.innerHTML = '';
                }
            } else {
                renderButton();
            }
        }
    }, [disabled]);

    if (!config.google.client_id) {
        return (
            <Button
                fullWidth
                variant="outlined"
                disabled={true}
                sx={{
                    textTransform: 'none',
                    borderColor: '#dadce0',
                    color: '#3c4043',
                }}
            >
                {text}
            </Button>
        );
    }

    return (
        <div className={classNames(className)}>
            <div ref={buttonRef} style={{ width: '100%', minHeight: '40px' }} />
        </div>
    );
}

export default GoogleSignInButton;

